import { Link } from "react-router-dom";
import { ShieldCheck, Award, Target, Users, ArrowRight, CheckCircle2, Heart, Building2, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicNav from "@/components/PublicNav";
import Seo from "@/components/Seo";
import PublicFooter from "@/components/PublicFooter";

export default function ForPartners() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Seo
        title="Partner with CareHomeConnect — high-quality senior living leads"
        description="Join CareHomeConnect's network of trusted senior living communities and placement directors. Get warm, qualified leads from families actively searching for senior care."
        path="/for-partners"
        keywords="senior living leads, assisted living leads, placement director leads, senior care lead generation, partner with CareHomeConnect"
      />
      <PublicNav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-12">
        <div className="animate-fade-in-up max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2C4C3B] text-[#F7F5F0] text-xs font-medium mb-6">
            <Award className="w-3.5 h-3.5" /> By invitation and application only
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A2E24] tracking-tight leading-[1.05]">
            A curated network of the senior care professionals we'd trust with our own parents.
          </h1>
          <p className="mt-6 text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
            CareHomeConnect is not a lead generation platform. We are a family-side advocacy service —
            and we only work with a small, vetted group of partners per region. Every application is reviewed personally by our team.
          </p>
        </div>
      </section>

      {/* Two paths */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <div className="text-sm text-[#9CAF88] font-semibold mb-3 uppercase tracking-wider">Choose your path</div>
        <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] mb-8 max-w-2xl">
          Which one are you?
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Facility card */}
          <div className="card-soft p-8 md:p-10 flex flex-col" data-testid="path-facility-card">
            <div className="w-14 h-14 rounded-2xl bg-[#9CAF88]/15 flex items-center justify-center mb-6">
              <Building2 className="w-6 h-6 text-[#2C4C3B]" />
            </div>
            <div className="text-xs font-semibold text-[#9CAF88] uppercase tracking-wider mb-2">Senior Care Communities</div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] mb-4 leading-tight">
              I operate a facility or community.
            </h3>
            <p className="text-[#5C6B62] leading-relaxed mb-6">
              Assisted living, memory care, independent living, or CCRC. You want to receive thoughtful, pre-qualified family
              introductions — not a firehose of low-quality leads.
            </p>
            <div className="space-y-2.5 mb-8">
              {[
                "Pre-screened family matches, not cold leads",
                "Limited partners per region (we cap the network on purpose)",
                "Built-in trust — our referrals come from families who trust us first",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#9CAF88] mt-0.5 flex-shrink-0" />
                  <span className="text-[#1A2E24]">{item}</span>
                </div>
              ))}
            </div>
            <Link to="/for-partners/apply?type=facility" className="mt-auto" data-testid="apply-facility">
              <Button className="w-full bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-12 font-medium group">
                Apply as a facility
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition" />
              </Button>
            </Link>
          </div>

          {/* Placement director card */}
          <div className="rounded-2xl p-8 md:p-10 flex flex-col bg-[#2C4C3B] border border-[#2C4C3B] text-white shadow-[0_8px_32px_rgba(44,76,59,0.15)]" data-testid="path-placement-card">
            <div className="w-14 h-14 rounded-2xl bg-[#9CAF88]/25 flex items-center justify-center mb-6">
              <Briefcase className="w-6 h-6 text-[#F7F5F0]" />
            </div>
            <div className="text-xs font-semibold text-[#9CAF88] uppercase tracking-wider mb-2">Placement Directors & Advisors</div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-white mb-4 leading-tight">
              I place families into senior care.
            </h3>
            <p className="text-[#F7F5F0]/80 leading-relaxed mb-6">
              You're an independent senior advisor, a placement agency, or a referral professional. Join a network that respects
              your judgment and gives you access to vetted families and communities both.
            </p>
            <div className="space-y-2.5 mb-8">
              {[
                "Access to our vetted facility network",
                "Family introductions aligned with your specialty",
                "Training, resources, and a peer community of top advisors",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#9CAF88] mt-0.5 flex-shrink-0" />
                  <span className="text-white">{item}</span>
                </div>
              ))}
            </div>
            <Link to="/for-partners/apply?type=placement_director" className="mt-auto" data-testid="apply-placement">
              <Button className="w-full bg-[#E07A5F] hover:bg-[#C96B52] text-white rounded-xl h-12 font-medium group">
                Apply as a placement director
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why we're different */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "We vet on your behalf", body: "Every partner in our network is reviewed for licensing, ethics, experience, and reputation — not just ad spend." },
            { icon: Target, title: "Limited partners per region", body: "We cap our network intentionally. Partners receive thoughtful matches, not firehose referrals." },
            { icon: Heart, title: "Family-first reputation", body: "Our referrals come from families who trust us. That trust carries into every introduction you receive." },
          ].map((item, i) => (
            <div key={i} className="card-soft p-8">
              <div className="w-11 h-11 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center mb-4">
                <item.icon className="w-5 h-5 text-[#2C4C3B]" />
              </div>
              <h3 className="font-display text-xl font-semibold text-[#1A2E24] mb-2">{item.title}</h3>
              <p className="text-sm text-[#5C6B62] leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="text-sm text-[#9CAF88] font-semibold mb-3 uppercase tracking-wider">The process</div>
        <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] mb-10 max-w-2xl">
          How we invite partners in.
        </h2>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { n: "01", title: "Apply", body: "A short, thoughtful application (about 3 minutes)." },
            { n: "02", title: "Review", body: "Our partnerships team reviews every application personally — typically within 3 business days." },
            { n: "03", title: "Conversation", body: "If it looks like a fit, we'll reach out for a short conversation. No pressure either way." },
            { n: "04", title: "Invitation", body: "Approved partners receive access and begin working with our vetted network." },
          ].map((step, i) => (
            <div key={i} className="card-soft p-6">
              <div className="font-display text-3xl text-[#9CAF88] mb-3">{step.n}</div>
              <h3 className="font-display text-lg font-semibold text-[#1A2E24] mb-2">{step.title}</h3>
              <p className="text-sm text-[#5C6B62] leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <div className="bg-white rounded-3xl p-10 md:p-14 border border-[#E2DFD8] text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9CAF88]/15 text-[#2C4C3B] text-xs font-medium mb-5">
            <Users className="w-3.5 h-3.5" /> We are currently reviewing applications for select regions
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] leading-tight max-w-2xl mx-auto">
            If you're someone we'd be proud to introduce, we'd love to meet you.
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/for-partners/apply?type=facility">
              <Button className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-12 px-8 font-medium">
                Apply as a facility
              </Button>
            </Link>
            <Link to="/for-partners/apply?type=placement_director">
              <Button variant="outline" className="border-[#E2DFD8] rounded-xl h-12 px-8 font-medium text-[#2C4C3B] hover:bg-[#9CAF88]/10">
                Apply as a placement director
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
